/**
 * Reseller home dashboard. Rendered in the chat-area zone when the
 * reseller hasn't selected a channel/DM. Pulls one round-trip from
 * GET /api/me/dashboard and renders three sections:
 *
 *   1. KPI trio — forwards, deliveries, orders in the last 7 days.
 *   2. Pending orders — top 5 orders still in flight (placed →
 *      shipped). Each row jumps to the orders modal.
 *   3. Recent CRM activity — last 5 orders linked to a CRM customer.
 *      Each row opens the customer detail in Settings → Клиенты.
 *
 * No editing happens here — the dashboard is an at-a-glance landing.
 * All deeper interactions delegate up via callbacks.
 */
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Mono, Serif, Btn } from "./ui.jsx";
import { useT } from "../i18n.jsx";

const STATUS_KEYS = {
  placed:      "dashboard.status.placed",
  confirmed:   "dashboard.status.confirmed",
  paid:        "dashboard.status.paid",
  purchased:   "dashboard.status.purchased",
  received:    "dashboard.status.received",
  shipped:     "dashboard.status.shipped",
  delivered:   "dashboard.status.delivered",
  cancelled:   "dashboard.status.cancelled",
  unavailable: "dashboard.status.unavailable",
};

const STATUS_COLOR = (s, ink56, persimmon) => {
  if (s === "delivered") return "#3a7d3a";
  if (s === "cancelled" || s === "unavailable") return ink56;
  return persimmon;
};

function humanAgo(unixSec, t) {
  if (!unixSec) return "";
  const secs = Math.max(0, Math.floor(Date.now() / 1000 - unixSec));
  if (secs < 60) return t("dashboard.row.now");
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} ${t("dashboard.unit.min")}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${t("dashboard.unit.hour")}`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${t("dashboard.unit.day")}`;
  const months = Math.floor(days / 30);
  return `${months} ${t("dashboard.unit.mo")}`;
}

export function Dashboard({
  user,
  channels = [],
  isMobile = false,
  onOpenSidebar,
  onOpenOrders,
  onOpenCustomers,
  onOpenNotifications,
  onBrowseChannels,
  onOpenForwardSettings,
}) {
  const t = useT();
  // Local unread-count poll — independent from App.jsx's bell badge
  // so the strip updates even when the user is sitting on this screen
  // and a notification arrives. Cheap call (partial-indexed COUNT).
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await api.getUnreadNotificationCount();
        if (!cancelled) setUnreadCount(r?.count ?? 0);
      } catch {}
    };
    pull();
    const iv = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Re-engagement suggestions — top 5 customers worth reaching out to.
  // The whole section disappears when there's nothing actionable so
  // we don't show the reseller a sad empty card on day one.
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getReEngagementSuggestions();
        if (!cancelled) setSuggestions(r?.items ?? []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getMyDashboard();
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center",
                    justifyContent: "center", color: T_NEW.ink56 }}>
        {t("dashboard.loading")}
      </div>
    );
  }
  if (err) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center",
                    justifyContent: "center", color: T_NEW.danger,
                    padding: 24, textAlign: "center" }}>
        {t("dashboard.loadError", { err })}
      </div>
    );
  }

  const kpi = data?.kpi ?? { forwards7d: 0, deliveries7d: 0, orders7d: 0 };
  const pending = data?.pendingOrders ?? [];
  const activity = data?.recentCustomerActivity ?? [];

  // First-run state — no forwards, no orders, no CRM customers yet.
  // Suggest two concrete next steps: configure the forward target and
  // browse channels.
  //
  // Gated to the customer role so admin/manager accounts (which also
  // see the dashboard for support previewing) don't get a reseller-
  // targeted "Настроить пересылку" CTA aimed at them. A real reseller
  // with literally no activity still sees the push; once they forward
  // even once, place an order, or link a CRM customer, the strip
  // correctly disappears.
  const isEmptyState =
    user?.role === "customer" &&
    kpi.forwards7d === 0 &&
    kpi.orders7d === 0 &&
    activity.length === 0;

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: T_NEW.paper,
      minHeight: 0,
    }}>
      {/* Mobile-only top bar with a hamburger that opens the sidebar
          drawer. Without this the user lands on the dashboard with no
          way to reach the channel list, settings, or anything else —
          the sidebar (which holds the 🏠 button + tabs) is offscreen
          on phones. */}
      {isMobile && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "calc(8px + env(safe-area-inset-top, 0px)) 12px 8px",
          background: T_NEW.paperRaised,
          borderBottom: `1px solid ${T_NEW.ink12}`,
          flexShrink: 0,
        }}>
          <button
            onClick={onOpenSidebar}
            aria-label={t("dashboard.menu")}
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              cursor: "pointer",
              fontSize: 22,
              lineHeight: 1,
              color: T_NEW.ink,
            }}>
            ☰
          </button>
          <Mono color={T_NEW.persimmon}
                style={{ fontWeight: 700 }}>
            {t("dashboard.kicker")}
          </Mono>
        </div>
      )}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "32px 28px 56px",
      }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Attention strip — surfaces unread notifications above the
            fold. Disappears once the reseller marks them read. */}
        {unreadCount > 0 && onOpenNotifications && (
          <div
            onClick={onOpenNotifications}
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              background: T_NEW.persimmonSoft,
              border: `1px solid ${T_NEW.persimmon}33`,
              borderRadius: T_NEW.r2,
              display: "flex", alignItems: "center", gap: 10,
              cursor: "pointer",
            }}>
            <span style={{
              width: 8, height: 8, borderRadius: 999,
              background: T_NEW.persimmon, flexShrink: 0,
            }} />
            <div style={{ flex: 1, fontSize: T_NEW.meta,
                          color: T_NEW.ink, lineHeight: 1.4 }}>
              <strong style={{ fontWeight: 700 }}>{unreadCount}</strong>{" "}
              {t("dashboard.attention.events")}
            </div>
            <Mono color={T_NEW.persimmon} size={T_NEW.micro}
                  style={{ fontWeight: 700 }}>
              {t("dashboard.attention.cta")}
            </Mono>
          </div>
        )}

        {/* Heading */}
        <div style={{ marginBottom: 24 }}>
          <Mono color={T_NEW.persimmon}
                style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
            {t("dashboard.kicker")}
          </Mono>
          <Serif size={T_NEW.hero}
                 style={{ fontWeight: 500, lineHeight: 1.1, color: T_NEW.ink,
                          letterSpacing: "-0.01em" }}>
            {greetingFor(user, t)}
          </Serif>
          <div style={{ marginTop: 6, fontSize: T_NEW.body,
                        color: T_NEW.ink56, lineHeight: 1.5,
                        fontFamily: T_NEW.fontSans }}>
            {t("dashboard.subtitle")}
          </div>
        </div>

        {/* KPI trio */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 28,
        }}>
          <KpiCard
            label={t("dashboard.kpi.forwards")}
            value={kpi.forwards7d}
            hint={kpi.forwards7d === 0
              ? t("dashboard.kpi.hint.noForwards")
              : t("dashboard.kpi.hint.forwardsOk", { n: kpi.deliveries7d })}
          />
          <KpiCard
            label={t("dashboard.kpi.delivered")}
            value={kpi.deliveries7d}
            hint={kpi.deliveries7d > 0
              ? t("dashboard.kpi.hint.deliveriesOk")
              : t("dashboard.kpi.hint.noDeliveries")}
          />
          <KpiCard
            label={t("dashboard.kpi.orders")}
            value={kpi.orders7d}
            hint={kpi.orders7d > 0
              ? t("dashboard.kpi.hint.ordersOk")
              : t("dashboard.kpi.hint.noOrders")}
          />
        </div>

        {/* Pending orders */}
        <Section
          title={t("dashboard.section.pending")}
          action={pending.length > 0
            ? { label: t("dashboard.section.pending.allOrders"), onClick: onOpenOrders }
            : null}
        >
          {pending.length === 0 ? (
            <Empty
              text={t("dashboard.section.pending.empty")}
              cta={kpi.orders7d === 0 && channels.length > 0 ? {
                label: t("dashboard.section.pending.browseCta"),
                onClick: onBrowseChannels,
              } : null}
            />
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {pending.map((o) => (
                <DashboardOrderRow key={o.id} order={o}
                                   onClick={onOpenOrders} t={t} />
              ))}
            </div>
          )}
        </Section>

        {/* Re-engagement suggestions — surfaces customers worth
            reaching out to today, with a one-word reason. The whole
            block hides when the suggestion API returns empty so a
            brand-new reseller doesn't see a placeholder. */}
        {suggestions.length > 0 && (
          <Section
            title={t("dashboard.section.reengage")}
            action={suggestions.length > 5
              ? { label: t("dashboard.section.reengage.allCustomers"), onClick: onOpenCustomers }
              : null}
          >
            <div style={{ display: "grid", gap: 8 }}>
              {suggestions.slice(0, 5).map((s) => (
                <SuggestionRow key={s.customer.id} item={s}
                               onOpen={onOpenCustomers} t={t} />
              ))}
            </div>
          </Section>
        )}

        {/* Recent CRM activity */}
        <Section
          title={t("dashboard.section.activity")}
          action={activity.length > 0
            ? { label: t("dashboard.section.reengage.allCustomers"), onClick: onOpenCustomers }
            : null}
        >
          {activity.length === 0 ? (
            <Empty
              text={t("dashboard.section.activity.empty")}
              cta={{ label: t("dashboard.section.activity.openCustomers"), onClick: onOpenCustomers }}
            />
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {activity.map((a) => (
                <DashboardActivityRow key={a.id} item={a}
                                      onClick={onOpenCustomers} t={t} />
              ))}
            </div>
          )}
        </Section>

        {/* First-run CTA strip — only when literally nothing has
            happened yet. Helps a brand-new reseller take the next
            obvious step without scrolling around. */}
        {isEmptyState && (
          <div style={{
            marginTop: 32, padding: "20px 22px",
            background: T_NEW.paperRaised,
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r3,
          }}>
            <Serif size={T_NEW.displaySm}
                   style={{ display: "block", marginBottom: 8,
                            fontWeight: 500 }}>
              {t("dashboard.emptyState.title")}
            </Serif>
            <div style={{ fontSize: T_NEW.body, color: T_NEW.ink56,
                          marginBottom: 14, lineHeight: 1.5,
                          fontFamily: T_NEW.fontSans }}>
              {t("dashboard.emptyState.body")}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn variant="accent" onClick={onOpenForwardSettings}>
                {t("dashboard.emptyState.setup")}
              </Btn>
              {channels.length > 0 && (
                <Btn variant="ghost" onClick={onBrowseChannels}>
                  {t("dashboard.emptyState.browse")}
                </Btn>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function greetingFor(user, t) {
  const hr = new Date().getHours();
  const period =
    hr < 5  ? t("dashboard.greeting.night")
    : hr < 12 ? t("dashboard.greeting.morning")
    : hr < 17 ? t("dashboard.greeting.day")
    : hr < 22 ? t("dashboard.greeting.evening")
    :           t("dashboard.greeting.night");
  const name = (user?.displayName || "").trim().split(/\s+/)[0];
  return name ? `${period}, ${name}.` : `${period}.`;
}

function KpiCard({ label, value, hint }) {
  return (
    <div style={{
      background: T_NEW.paperRaised,
      border: `1px solid ${T_NEW.ink12}`,
      borderRadius: T_NEW.r2,
      padding: "16px 18px",
    }}>
      <Mono color={T_NEW.ink56} size={T_NEW.micro}
            style={{ display: "block", marginBottom: 8 }}>
        {label}
      </Mono>
      <div style={{
        fontFamily: T_NEW.fontSerif,
        fontSize: T_NEW.displayLg,
        fontWeight: 500,
        color: T_NEW.ink,
        lineHeight: 1,
      }}>
        {value}
      </div>
      {hint && (
        <Mono color={T_NEW.ink38} size={T_NEW.micro}
              style={{ display: "block", marginTop: 8, lineHeight: 1.3 }}>
          {hint}
        </Mono>
      )}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "baseline", marginBottom: 10 }}>
        <Serif size={T_NEW.displaySm}
               style={{ fontWeight: 500, color: T_NEW.ink }}>
          {title}
        </Serif>
        {action && (
          <button
            onClick={action.onClick}
            style={{
              background: "transparent", border: "none",
              color: T_NEW.persimmon,
              fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
              letterSpacing: T_NEW.monoTrack, textTransform: "uppercase",
              fontWeight: 700, cursor: "pointer", padding: 0,
            }}>
            {action.label} →
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ text, cta }) {
  return (
    <div style={{
      padding: "20px 18px",
      border: `1px dashed ${T_NEW.ink12}`,
      borderRadius: T_NEW.r2,
      background: T_NEW.paperSoft,
      textAlign: "center",
    }}>
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                    marginBottom: cta ? 12 : 0, lineHeight: 1.5,
                    fontFamily: T_NEW.fontSans }}>
        {text}
      </div>
      {cta && (
        <button
          onClick={cta.onClick}
          style={{
            background: "transparent",
            color: T_NEW.persimmon,
            border: `1px solid ${T_NEW.ink12}`,
            padding: "8px 14px",
            borderRadius: T_NEW.r1,
            fontFamily: T_NEW.fontMono,
            fontSize: T_NEW.micro,
            letterSpacing: T_NEW.monoTrack,
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
          }}>
          {cta.label}
        </button>
      )}
    </div>
  );
}

// Single re-engagement suggestion row. Compact — name + reason text +
// arrow CTA. onOpen jumps to the customers tab; the reseller picks
// the customer from there. Future iteration: deep-link to that
// specific customer's detail modal (would need MyStore to accept an
// initialCustomerId prop).
function SuggestionRow({ item, onOpen, t }) {
  const { customer, reason } = item;
  return (
    <div
      onClick={onOpen}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 12px",
        background: T_NEW.paperRaised,
        border: `1px solid ${T_NEW.ink12}`,
        borderLeft: `3px solid ${T_NEW.persimmon}`,
        borderRadius: T_NEW.r2,
        cursor: "pointer",
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                      fontWeight: 500,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis" }}>
          {customer.name}
        </div>
        <Mono color={T_NEW.persimmon} size={T_NEW.micro}
              style={{ display: "block", marginTop: 2,
                       fontWeight: 700 }}>
          {reason}
        </Mono>
      </div>
      {customer.telegramHandle && (
        <Mono color={T_NEW.ink56} size={T_NEW.micro}
              style={{ flexShrink: 0 }}>
          {customer.telegramHandle}
        </Mono>
      )}
      <span style={{ flexShrink: 0, color: T_NEW.persimmon,
                     fontFamily: T_NEW.fontMono,
                     fontSize: T_NEW.micro,
                     letterSpacing: T_NEW.monoTrack,
                     textTransform: "uppercase",
                     fontWeight: 700 }}>
        {t("dashboard.row.openCta")}
      </span>
    </div>
  );
}

function DashboardOrderRow({ order, onClick, t }) {
  const placed = order.placedAt ? new Date(order.placedAt * 1000) : null;
  const statusColor = STATUS_COLOR(order.status, T_NEW.ink56, T_NEW.persimmon);
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 12px",
        background: T_NEW.paperRaised,
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: T_NEW.r2,
        cursor: "pointer",
      }}>
      {order.post?.imageUrl ? (
        <img src={order.post.imageUrl} alt=""
             style={{ width: 48, height: 48, objectFit: "cover",
                      borderRadius: T_NEW.r1, flexShrink: 0,
                      background: T_NEW.paperSoft }} />
      ) : (
        <div style={{ width: 48, height: 48, flexShrink: 0,
                      background: T_NEW.paperSoft,
                      borderRadius: T_NEW.r1 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis", fontWeight: 500 }}>
          {order.post?.title || t("dashboard.row.untitled")}
        </div>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}>
          {[
            order.resellerCustomer?.name,
            placed && humanAgo(order.placedAt, t) + " " + t("dashboard.row.ago"),
          ].filter(Boolean).join(" · ")}
        </Mono>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      fontWeight: 600 }}>
          ${(order.totalUsdCents / 100).toFixed(2)}
        </div>
        <Mono color={statusColor} size={T_NEW.micro}
              style={{ display: "block", marginTop: 2, fontWeight: 700 }}>
          {STATUS_KEYS[order.status] ? t(STATUS_KEYS[order.status]) : order.status}
        </Mono>
      </div>
    </div>
  );
}

function DashboardActivityRow({ item, onClick, t }) {
  const placed = item.placedAt ? new Date(item.placedAt * 1000) : null;
  const statusColor = STATUS_COLOR(item.status, T_NEW.ink56, T_NEW.persimmon);
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 12px",
        background: T_NEW.paperRaised,
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: T_NEW.r2,
        cursor: "pointer",
      }}>
      {item.post?.imageUrl ? (
        <img src={item.post.imageUrl} alt=""
             style={{ width: 48, height: 48, objectFit: "cover",
                      borderRadius: T_NEW.r1, flexShrink: 0,
                      background: T_NEW.paperSoft }} />
      ) : (
        <div style={{ width: 48, height: 48, flexShrink: 0,
                      background: T_NEW.paperSoft,
                      borderRadius: T_NEW.r1 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis", fontWeight: 500 }}>
          {item.customer.name}
        </div>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}
              style={{ display: "block",
                       whiteSpace: "nowrap", overflow: "hidden",
                       textOverflow: "ellipsis" }}>
          {item.post?.title || t("dashboard.row.untitled")}
        </Mono>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      fontWeight: 600 }}>
          ×{item.qty} · ${(item.totalUsdCents / 100).toFixed(2)}
        </div>
        <Mono color={statusColor} size={T_NEW.micro}
              style={{ display: "block", marginTop: 2 }}>
          {placed ? humanAgo(item.placedAt, t) + " " + t("dashboard.row.ago")
                  : (STATUS_KEYS[item.status] ? t(STATUS_KEYS[item.status]) : item.status)}
        </Mono>
      </div>
    </div>
  );
}
